import React from 'react';
import { Box, Typography } from '@mui/material';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// What the CanRisk form's cancer ages were read from, and what looked like one
// of the four cancers but was not counted, with why (TGCLOUD-1892).
//
// The form counts a cancer by its code (backend/util/canriskCancers.js): a
// syndrome or a susceptibility never counts, and nor does a code outside the
// reviewed set. Without this list a clinician could not see that "Hereditary
// breast and/or ovarian cancer syndrome" was left out on purpose, or that a
// breast cancer was recorded with no age, and so could not add the age by hand
// where it is right to.

export const CANCER_LABELS = Object.freeze({
  BC: 'Breast cancer',
  OC: 'Ovarian cancer',
  PRO: 'Prostate cancer',
  PAN: 'Pancreatic cancer'
});

// The sentence for each reason a diagnosis did not count. English keys; the
// component translates them where they are rendered.
export const NOT_COUNTED_REASONS = Object.freeze({
  syndrome: 'A syndrome or susceptibility, not a cancer the person has had.',
  // Kathryn's sign-off, 2026-10-09: a hereditary entry records the family
  // history, not a diagnosis of the cancer itself.
  'family-history':
    'A hereditary or family-history entry, not a diagnosis of the cancer itself. Add the age by hand if the person has had this cancer.',
  'code-not-in-set': 'Its code is not one CanRisk counts as this cancer.',
  'other-disease':
    'Recorded as Other disease. Add the age by hand if it is this cancer.',
  status: 'Its status says the person does not have it.'
});

/** The form's two lists, from one person's cancerEvidence. Pure, for tests. */
export function splitCancerEvidence(evidence) {
  const list = Array.isArray(evidence) ? evidence : [];
  return {
    counted: list.filter((e) => e.counted),
    notCounted: list.filter((e) => !e.counted && e.reason)
  };
}

export default function CanRiskCancerEvidence({ evidence }) {
  const t = useAutoTranslation();
  const { counted, notCounted } = splitCancerEvidence(evidence);
  if (!counted.length && !notCounted.length) return null;
  const codeOf = (e) => (e.id ? ` (${e.id})` : '');

  return (
    <Box sx={{ mt: 1.5 }} data-testid="canrisk-cancer-evidence">
      {counted.length > 0 && (
        <>
          <Typography
            variant="caption"
            sx={{ display: 'block', fontWeight: 600 }}
          >
            {t('Read from the record')}
          </Typography>
          {counted.map((e, i) => (
            <Typography
              key={`c-${e.id}-${i}`}
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary' }}
            >
              {e.age === null
                ? t('{{cancer}}: {{name}}{{code}}, no age recorded', {
                    cancer: t(CANCER_LABELS[e.kind]),
                    name: e.name || '',
                    code: codeOf(e)
                  })
                : t('{{cancer}}: {{name}}{{code}}, age {{age}}', {
                    cancer: t(CANCER_LABELS[e.kind]),
                    name: e.name || '',
                    code: codeOf(e),
                    age: e.age
                  })}
            </Typography>
          ))}
        </>
      )}
      {notCounted.length > 0 && (
        <>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 600,
              mt: counted.length ? 1 : 0
            }}
          >
            {t('Not counted')}
          </Typography>
          {notCounted.map((e, i) => (
            <Typography
              key={`n-${e.id}-${i}`}
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary' }}
            >
              {t('{{name}}{{code}}, looks like {{cancer}}: {{reason}}', {
                name: e.name || '',
                code: codeOf(e),
                cancer: t(CANCER_LABELS[e.looksLike] || 'a cancer'),
                reason: t(NOT_COUNTED_REASONS[e.reason])
              })}
            </Typography>
          ))}
        </>
      )}
    </Box>
  );
}
